import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronLeft, faAngleDoubleRight, faAngleDoubleLeft } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            onPageChange(newPage);
        }
    };

    if (totalPages <= 1) return null;

    // Generate page numbers to display
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            // Show all pages if total pages is less than max visible
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);

            // Calculate start and end of visible pages
            let start = Math.max(2, currentPage - 1);
            let end = Math.min(totalPages - 1, currentPage + 1);

            // Adjust if at the start
            if (currentPage <= 2) {
                end = 4;
            }
            // Adjust if at the end
            if (currentPage >= totalPages - 1) {
                start = totalPages - 3;
            }

            // Add ellipsis if needed
            if (start > 2) {
                pages.push('...');
            }

            // Add middle pages
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            // Add ellipsis if needed
            if (end < totalPages - 1) {
                pages.push('...');
            }

            // Always show last page
            pages.push(totalPages);
        }

        return pages;
    };

    return (
        <div className="pagination-container">
            <div className="pagination">
                <motion.button
                    className="nav-btn first-page"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    whileHover={{ scale: 1.05, backgroundColor: '#f1f5f9' }}
                    whileTap={{ scale: 0.95 }}
                >
                    <FontAwesomeIcon icon={faAngleDoubleRight} />
                </motion.button>

                <motion.button
                    className="nav-btn prev-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    whileHover={{ scale: 1.05, backgroundColor: '#f1f5f9' }}
                    whileTap={{ scale: 0.95 }}
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </motion.button>

                <div className="page-numbers">
                    <AnimatePresence mode="wait">
                        {getPageNumbers().map((page, index) => (
                            page === '...' ? (
                                <motion.span
                                    key={`ellipsis-${index}`}
                                    className="ellipsis"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                >
                                    ...
                                </motion.span>
                            ) : (
                                <motion.button
                                    key={page}
                                    className={`page-number ${currentPage === page ? 'active' : ''}`}
                                    onClick={() => handlePageChange(page)}
                                    whileHover={{
                                        scale: 1.1,
                                        backgroundColor: currentPage === page ? '#4a90e2' : '#f1f5f9'
                                    }}
                                    whileTap={{ scale: 0.9 }}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                >
                                    {page}
                                </motion.button>
                            )
                        ))}
                    </AnimatePresence>
                </div>

                <motion.button
                    className="nav-btn next-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    whileHover={{ scale: 1.05, backgroundColor: '#f1f5f9' }}
                    whileTap={{ scale: 0.95 }}
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </motion.button>

                <motion.button
                    className="nav-btn last-page"
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    whileHover={{ scale: 1.05, backgroundColor: '#f1f5f9' }}
                    whileTap={{ scale: 0.95 }}
                >
                    <FontAwesomeIcon icon={faAngleDoubleLeft} />
                </motion.button>
            </div>

            <div className="page-info">
                <span className="current-page">{currentPage}</span>
                <span className="separator">/</span>
                <span className="total-pages">{totalPages}</span>
            </div>

            <style jsx>{`
                .pagination-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 16px;
                    margin-top: 32px;
                }

                .pagination {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px;
                    background: #fff;
                    border-radius: 20px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
                }

                .nav-btn {
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: none;
                    border-radius: 12px;
                    background: transparent;
                    color: #64748b;
                    cursor: pointer;
                    transition: all 0.3s ease;
                      font-family: "Zain", sans-serif;
                }

                .nav-btn:hover:not(:disabled) {
                    color: #6366f1;
                }

                .nav-btn:disabled {
                    opacity: 0.4;
                    cursor: not-allowed;
                }

                .nav-btn svg {
                    width: 16px;
                    height: 16px;
                }

                .page-numbers {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    padding: 0 8px;
                }

                .page-number {
                    min-width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: none;
                    border-radius: 12px;
                    background: transparent;
                    color: #64748b;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.3s ease;
                      font-family: "Zain", sans-serif;
                }

                .page-number:hover {
                    color: #6366f1;
                }

                .page-number.active {
                    background: #6366f1;
                    color: #fff;
                }

                .ellipsis {
                    color: #64748b;
                    font-size: 14px;
                    font-weight: 500;
                    padding: 0 8px;
                }

                .page-info {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                      font-family: "Zain", sans-serif;
                }

                .current-page {
                    font-size: 18px;
                    font-weight: 600;
                    color: #4a90e2;
                }

                .separator {
                    font-size: 16px;
                    color: #64748b;
                }

                .total-pages {
                    font-size: 16px;
                    color: #64748b;
                }

                @media (max-width: 768px) {
                    .pagination {
                        padding: 6px;
                        gap: 4px;
                    }
                    .nav-btn, .page-number {
                        width: 36px;
                        height: 36px;
                    }
                    .page-number {
                        min-width: 36px;
                    }
                    .current-page {
                        font-size: 16px;
                    }
                    .separator, .total-pages {
                        font-size: 14px;
                    }
                }

                @media (max-width: 600px) {
                    .pagination-container {
                        margin-top: 16px;
                        gap: 8px;
                    }
                    .pagination {
                        padding: 4px;
                        gap: 2px;
                        border-radius: 12px;
                    }
                    .nav-btn, .page-number {
                        width: 28px;
                        height: 28px;
                        font-size: 12px;
                        border-radius: 6px;
                    }
                    .page-number {
                        min-width: 28px;
                        font-size: 12px;
                    }
                    .page-numbers {
                        gap: 2px;
                        padding: 0 2px;
                        overflow-x: auto;
                        max-width: 180px;
                    }
                    .ellipsis {
                        font-size: 12px;
                        padding: 0 2px;
                    }
                    .current-page {
                        font-size: 14px;
                    }
                    .separator, .total-pages {
                        font-size: 12px;
                    }
                }
            `}</style>
        </div>
    );
};

export default Pagination; 